import { EnabledProctoringOptions } from "@verity/api-client/types";
import { ProctoringAssessmentConfigurationV2, ProctoringMethod } from "@verity/proctoring-option/types";
import {
	CoreChip,
	CoreModal,
	CoreModalDescription,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
	CoreSkeletonWrapper,
} from "../../core";
import { useState } from "react";
import i18nPackage from "../../i18n/i18n";
import { Typography } from "@mui/material";
import styles from "./MethodStep.module.css";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { VerityInstituteSettings } from "../../types/common";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "../hooks/state/useEditQuizState";
import { METHOD_ICON_CONFIG } from "./StepIconConfig";

function DeleteLiveSessionsModal({ onClose }: { onClose: () => void }) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18nPackage.t("editQuizPage.methodStep.deleteModalTitle")}
			showBottomCloseButton
			maxWidth="500px"
		>
			<CoreModalDescription>{i18nPackage.t("editQuizPage.methodStep.deleteUpcomingSessions")}</CoreModalDescription>
		</CoreModal>
	);
}

type MethodButtonProps = {
	type: ProctoringMethod;
	selected: boolean;
	onClick: () => void;
	loading?: boolean;
};

function MethodButton({ selected, type, onClick, loading }: MethodButtonProps) {
	const translationKey = type === "pop-in" ? "liveCheckIn" : type === "pop-in-post-review" ? "postReview" : type;
	const MethodIcon = METHOD_ICON_CONFIG[type];

	return (
		<CoreSkeletonWrapper loading={loading} height={"100%"}>
			<CoreRadioCard slotProps={{ button: { className: styles.methodButton } }} onClick={onClick} selected={selected}>
				<div className={styles.badgeRow}>
					<CoreChip chipColor="#F3E5F5" className={styles.methodBadge}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.badge`)}
					</CoreChip>
					<CoreRadioCardSelectIcon selected={selected} />
				</div>
				<div className={styles.methodColumn}>
					<CoreRadioCardIcon icon={<MethodIcon />} enabled={selected} width={60} height={60} />
					<Typography variant="h2" className={styles.methodName}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.header`)}
					</Typography>
					<Typography className={styles.methodDescription}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.description`)}
					</Typography>
				</div>
			</CoreRadioCard>
		</CoreSkeletonWrapper>
	);
}

type MethodStepProps = {
	enabledProctoringOptions: EnabledProctoringOptions;
	updateOptions: (newOptions: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">) => void;
	hasUpcomingSessions: () => boolean;
	verityInstituteSettings: VerityInstituteSettings;
	options: NonNullable<EditQuizPageProps["proctoringAssessmentConfiguration"]>;
	loading?: boolean;
} & Pick<UseEditQuizStateReturn["nav"]["action"], "unmarkStepAsCompleted">;

export default function MethodStep({
	enabledProctoringOptions,
	updateOptions,
	hasUpcomingSessions,
	unmarkStepAsCompleted,
	options,
	verityInstituteSettings,
	loading,
}: MethodStepProps) {
	const [deleteSessionsModalShowing, setDeleteSessionsModalShowing] = useState(false);

	const setLiveMode = (mode: ProctoringMethod) => {
		if (mode !== "live" && options.isLiveProctored && hasUpcomingSessions()) {
			setDeleteSessionsModalShowing(true);
		} else {
			updateOptions({
				...options,
				isLiveProctored: mode === "live",
				isLivePopIn: mode === "pop-in" || mode === "pop-in-post-review",
				enablePostReview: mode === "pop-in-post-review",
			});
		}
	};

	const disableAutomated = !enabledProctoringOptions.proctoringMethod.includes("automated");

	const disableLive =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLiveProctoring ||
		!enabledProctoringOptions.proctoringMethod.includes("live");

	const disablePopIn =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLivePopIn ||
		!enabledProctoringOptions.proctoringMethod.includes("pop-in");

	const disablePopInPostReview =
		verityInstituteSettings.liteMode ||
		!verityInstituteSettings.allowLivePopIn ||
		!verityInstituteSettings.allowLivePopInPostReview ||
		!enabledProctoringOptions.proctoringMethod.includes("pop-in-post-review");

	return (
		<StepWrapper>
			<div className={styles.methodContainer}>
				<StepDescription loading={loading}>{i18nPackage.t("editQuizPage.methodStep.description")}</StepDescription>

				<div className={styles.methodGrid}>
					{!disableAutomated && (
						<MethodButton
							type="automated"
							onClick={() => {
								if (options.isLiveProctored || options.isLivePopIn) {
									unmarkStepAsCompleted("liveSessions");
									setLiveMode("automated");
								}
							}}
							selected={!options.isLiveProctored && !options.isLivePopIn}
							loading={loading}
						/>
					)}
					{!disableLive && (
						<MethodButton
							type="live"
							onClick={() => {
								if (!options.isLiveProctored) {
									setLiveMode("live");
								}
							}}
							selected={options.isLiveProctored ?? false}
							loading={loading}
						/>
					)}
					{!disablePopIn && (
						<MethodButton
							type="pop-in"
							onClick={() => {
								if (!options.isLivePopIn) {
									unmarkStepAsCompleted("liveSessions");
									setLiveMode("pop-in");
								}
							}}
							selected={(options.isLivePopIn && !options.enablePostReview) ?? false}
							loading={loading}
						/>
					)}
					{!disablePopInPostReview && (
						<MethodButton
							type="pop-in-post-review"
							onClick={() => {
								if (!options.isLivePopIn || !options.enablePostReview) {
									if (!options.isLivePopIn) {
										unmarkStepAsCompleted("liveSessions");
									}
									setLiveMode("pop-in-post-review");
								}
							}}
							selected={(options.isLivePopIn && options.enablePostReview) ?? false}
							loading={loading}
						/>
					)}
				</div>
				{deleteSessionsModalShowing && <DeleteLiveSessionsModal onClose={() => setDeleteSessionsModalShowing(false)} />}
			</div>
		</StepWrapper>
	);
}
